import React, { useEffect, useState, useCallback } from "react";
import { Play, X } from "lucide-react";
import { heroSlides } from "../mock";

const LOGO_IMAGE = "/assets/images/logo-landing.png";
const SCHOOL_BG = "/assets/images/school.jpg";
const GOVERNOR_IMAGE = "/assets/images/governor.png";
const HERO_BG = "/assets/images/herobg.jpeg";

const SlideBackground = ({ slide, active }) => {
  // ================= LOGO SECTION =================
  if (slide.kind === "full-logo") {
    return (
      <div className="absolute inset-0 overflow-hidden">
        <img
          src={HERO_BG}
          alt="AKSUBEB Background"
          className={`absolute inset-0 w-full h-full object-cover object-center ${active ? "kb-zoom-soft" : ""}`}
        />
        <div className="absolute inset-0 bg-white/90" />
        <div className="absolute inset-0 backdrop-blur-[1px]" />

        {/* DESKTOP */}
        <div className="hidden md:flex h-full items-center justify-center px-8 relative z-10">
          <div className="w-full max-w-[1350px] flex items-center justify-between gap-10">
            <div className="flex-shrink-0">
              <img
                src={LOGO_IMAGE}
                alt="AKSUBEB"
                className={`w-[380px] lg:w-[560px] object-contain ${active ? "kb-zoom-soft" : ""}`}
              />
            </div>
            <div className="flex-1">
              <h2
                className="text-[#06183f] font-black text-[75px] lg:text-[90px] leading-none mb-5"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                Welcome to
              </h2>
              <h1
                className="text-[#1f9d4c] font-black leading-[0.95] tracking-[-3px] text-[50px] lg:text-[70px]"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                AKWA IBOM STATE
                <br />
                UNIVERSAL BASIC
                <br />
                EDUCATION BOARD
              </h1>
            </div>
          </div>
        </div>

        {/* MOBILE */}
        <div className="md:hidden h-full flex flex-col items-center justify-center px-6 text-center relative z-10">
          <img
            src={LOGO_IMAGE}
            alt="AKSUBEB"
            className={`w-[180px] sm:w-[240px] object-contain mb-8 ${active ? "kb-zoom-soft" : ""}`}
          />
          <h2
            className="text-[#06183f] font-black text-[40px] leading-none mb-4"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            Welcome to
          </h2>
          <h1
            className="text-[#1f9d4c] font-black leading-[1] tracking-[-1px] text-[40px]"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            AKWA IBOM STATE
            <br />
            UNIVERSAL BASIC
            <br />
            EDUCATION BOARD
          </h1>
        </div>
      </div>
    );
  }

  // ================= GOVERNOR SECTION =================
  if (slide.kind === "full-image") {
    return (
      <div className="absolute inset-0 overflow-hidden bg-[#ececf5]">
        <img
          src={GOVERNOR_IMAGE}
          alt="Pastor Umo Eno"
          className={`w-full h-full object-cover object-center ${active ? "kb-zoom-soft" : ""}`}
        />
        <div className="absolute inset-0 bg-black/18" />

        <div className="absolute inset-x-0 bottom-[95px] z-10">
          <div className="text-center px-6">
            <h1
              className="text-white font-black drop-shadow-2xl text-[42px] md:text-[78px] lg:text-[105px] leading-[0.9] tracking-[-4px]"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Pastor Umo Eno
            </h1>
            <p className="mt-2 text-white text-[18px] md:text-[34px] font-medium">
              Executive Governor, Akwa Ibom State
            </p>
            <p
              className="mt-5 text-white italic font-medium text-[15px] md:text-[24px] lg:text-[28px] max-w-[950px] mx-auto leading-relaxed drop-shadow-xl"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              “Education remains the greatest gift we can give the next generation.”
              <span className="block mt-2 font-bold not-italic">- Gov Umo Eno, PhD</span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ================= VIDEO / SCHOOL SECTION =================
  return (
    <div className="absolute inset-0 overflow-hidden">
      <img
        src={SCHOOL_BG}
        alt="AKSUBEB"
        className={`w-full h-full object-cover object-center ${active ? "kb-zoom" : ""}`}
      />
      <div className="absolute inset-0 bg-black/30" />
    </div>
  );
};

const HeroSlider = () => {
  const [current, setCurrent] = useState(0);
  const [videoOpen, setVideoOpen] = useState(false);

  // ================= COMMENTED OUT INFRASTRUCTURE SLIDE =================
  const filteredSlides = heroSlides.filter((slide) => slide.kind !== "video");

  const len = filteredSlides.length;

  const next = useCallback(() => {
    setCurrent((c) => (c + 1) % len);
  }, [len]);

  useEffect(() => {
    const id = setInterval(next, 7000);
    return () => clearInterval(id);
  }, [next]);

  const activeSlide = filteredSlides[current];

  return (
    <section className="relative overflow-visible bg-white">
      <div className="relative h-[520px] sm:h-[620px] md:h-[720px] lg:h-[820px]">
        {filteredSlides.map((slide, idx) => {
          const active = idx === current;

          return (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                active ? "opacity-100 z-10" : "opacity-0 z-0"
              }`}
              aria-hidden={!active}
            >
              <SlideBackground slide={slide} active={active} />

              {slide.kind === "video" && (
                <div className="absolute inset-0 flex items-center">
                  <div className="max-w-[1280px] mx-auto px-6 lg:px-12 w-full">
                    <button
                      onClick={() => setVideoOpen(true)}
                      className="group relative mb-8 inline-flex items-center justify-center"
                      aria-label="Play video"
                    >
                      <span className="absolute inset-0 rounded-full bg-white/30 blur-md group-hover:bg-white/50 transition-colors" />
                      <span className="relative w-14 h-14 md:w-[72px] md:h-[72px] rounded-full bg-white/95 group-hover:bg-white text-[#0a2463] flex items-center justify-center shadow-2xl transition-all group-hover:scale-110">
                        <Play size={24} fill="#0a2463" className="ml-1" />
                      </span>
                    </button>
                    <h1
                      className="text-white font-black drop-shadow-2xl text-[38px] sm:text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tight max-w-[900px]"
                      style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                    >
                      {slide.overlayText}
                    </h1>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* ================= SLIDER CIRCLES ================= */}
        <div className="absolute left-1/2 -translate-x-1/2 -bottom-[42px] z-30 flex items-center gap-3 md:gap-4">
          {filteredSlides.map((s, i) => {
            const isCurrent = i === current;

            let thumbImage = SCHOOL_BG;
            if (s.kind === "full-logo") thumbImage = LOGO_IMAGE;
            if (s.kind === "full-image") thumbImage = GOVERNOR_IMAGE;

            return (
              <button
                key={s.id}
                onClick={() => setCurrent(i)}
                className={`overflow-hidden rounded-full transition-all duration-300 bg-white shadow-2xl flex items-center justify-center ${
                  isCurrent
                    ? "w-[82px] h-[82px] md:w-[96px] md:h-[96px] border-[4px] border-[#0d2d7a]"
                    : "w-[72px] h-[72px] md:w-[86px] md:h-[86px] border-[3px] border-white"
                }`}
                aria-label={`Go to slide ${i + 1}`}
              >
                <img src={thumbImage} alt="" className="w-full h-full object-cover rounded-full" />
              </button>
            );
          })}
        </div>
      </div>

      {/* ================= VIDEO MODAL ================= */}
      {videoOpen && activeSlide?.kind === "video" && (
        <div
          onClick={() => setVideoOpen(false)}
          className="fixed inset-0 bg-black/85 z-[100] flex items-center justify-center p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-5xl aspect-video bg-black shadow-2xl"
          >
            <button
              onClick={() => setVideoOpen(false)}
              className="absolute -top-12 right-0 text-white hover:text-[#ed6a2c] transition-colors"
              aria-label="Close"
            >
              <X size={30} />
            </button>
            <iframe
              className="w-full h-full"
              src={activeSlide.videoUrl}
              title="AKSUBEB Video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default HeroSlider;
